import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useJazyk } from '../../context/LanguageContext';
import { useContactEmail } from '../../hooks/useContactEmail';
import { useTextSettings } from '../../hooks/useTextSettings';
import { useLocations } from '../../hooks/useLocations';
import { avatarOptions } from '../avatarSystem';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { toast } from 'sonner';
import { Eye, EyeOff, User, Mail, Lock, MapPin, Camera, KeyRound, Phone } from 'lucide-react';
import { API } from '../../lib/api';

const pronounOptions = [
  { value: 'ona/její', label: 'ona/její' }, { value: 'on/jeho', label: 'on/jeho' },
  { value: 'oni/jejich', label: 'oni/jejich' }, { value: 'ono/jeho', label: 'ono/jeho' }, { value: 'jiné', label: 'jiné' },
];

const GOOGLE_SVG = (
  <svg className="w-4 h-4" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Vrací klíč do slovníku, ne hotovou větu: text se přeloží až tam, kde se
// ukazuje, a funkce nemusí vědět nic o jazyku.
function validatePassword(password) {
  if (password.length < 8) return 'reg.hesloKratke';
  if (!/[a-z]/.test(password)) return 'reg.hesloMale';
  if (!/[A-Z]/.test(password)) return 'reg.hesloVelke';
  if (!/[0-9]/.test(password)) return 'reg.hesloCislo';
  if (!/[^a-zA-Z0-9]/.test(password)) return 'reg.hesloZnak';
  return null;
}

export function RegisterForm({ loading, setLoading }) {
  const { register } = useAuth();
  const { t } = useJazyk();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const contactEmail = useContactEmail();
  const texts = useTextSettings();
  const { allLocations } = useLocations();
  const [regCountry, setRegCountry] = useState('CZ');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordHelp, setShowPasswordHelp] = useState(false);
  const [entryPasswordRequired, setEntryPasswordRequired] = useState(true);
  const [customAvatarPreview, setCustomAvatarPreview] = useState(null);
  const [reg, setReg] = useState({
    email: '', password: '', passwordConfirm: '', username: '', pronouns: 'ona/její',
    avatar: 'fem-pink', location: 'none', district: 'none', phone: '', bio: '', secretCode: '', website: '',
  });

  useEffect(() => {
    axios.get(`${API}/settings/entry-password-status`)
      .then(r => setEntryPasswordRequired(r.data.enabled))
      .catch(() => setEntryPasswordRequired(true));
  }, []);

  const updateReg = (field, value) => setReg(prev => ({ ...prev, [field]: value }));

  const handleRegister = async (e) => {
    e.preventDefault();
    const pwErr = validatePassword(reg.password);
    if (pwErr) { toast.error(t(pwErr)); return; }
    if (reg.password !== reg.passwordConfirm) { toast.error(t('reg.hesloNesouhlasi')); return; }
    if (reg.username.length < 3) { toast.error(t('reg.prezdivkaKratka')); return; }
    if (entryPasswordRequired && !reg.secretCode) { toast.error(t('reg.hesloPovinne')); return; }
    setLoading(true);
    try {
      await register({
        email: reg.email, password: reg.password, username: reg.username,
        pronouns: reg.pronouns, avatar: reg.avatar,
        location: reg.location === 'none' ? '' : reg.location,
        district: reg.district === 'none' ? '' : reg.district,
        phone: reg.phone, bio: reg.bio, secret_code: reg.secretCode,
        custom_avatar: customAvatarPreview, website: reg.website || ''
      });
      toast.success(t('reg.hotovo'));
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('reg.chyba'));
    } finally { setLoading(false); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t('reg.fotoVelka')); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setCustomAvatarPreview(ev.target.result); updateReg('avatar', 'custom'); };
    reader.readAsDataURL(file);
  };

  const avatarGroups = [
    { label: t('reg.avatarFem'), avatars: avatarOptions.filter(a => a.gender === 'feminine'), color: 'text-bloom-pride-pink' },
    { label: t('reg.avatarMask'), avatars: avatarOptions.filter(a => a.gender === 'masculine'), color: 'text-bloom-pride-blue' },
    { label: t('reg.avatarNeutr'), avatars: avatarOptions.filter(a => a.gender === 'neutral'), color: 'text-bloom-mint' },
    { label: t('reg.avatarZvirata'), avatars: avatarOptions.filter(a => a.gender === 'animal'), color: 'text-amber-500' },
    { label: t('reg.avatarSymboly'), avatars: avatarOptions.filter(a => a.gender === 'symbol'), color: 'text-bloom-violet' },
  ].filter(g => g.avatars.length > 0);

  return (
    <>
      <form onSubmit={handleRegister} className="space-y-4">
        {entryPasswordRequired && (
          <div className="space-y-2">
            <Label className="text-bloom-violet font-semibold">{t('reg.komunitniHeslo')}</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-violet/50" />
              <Input type="password" placeholder={t('reg.komunitniHesloPriklad')} value={reg.secretCode} onChange={(e) => updateReg('secretCode', e.target.value)} className="pl-10 border-bloom-violet/30 bg-bloom-violet/5" required data-testid="register-secret-input" />
            </div>
            <div className="bg-bloom-violet/5 border border-bloom-violet/15 rounded-lg p-3 space-y-1.5">
              <p className="text-xs text-bloom-sub leading-relaxed">
                {texts.help_text || t('reg.komunitniHesloPopis')}
              </p>
              <p className="text-xs text-bloom-sub leading-relaxed">
                {t('reg.napisAdminovi')}{' '}
                {contactEmail && <a href={`mailto:${contactEmail}`} className="text-bloom-violet hover:underline font-medium">{contactEmail}</a>}.
              </p>
              <button type="button" onClick={() => setShowPasswordHelp(p => !p)} className="text-xs text-bloom-violet/70 hover:text-bloom-violet underline underline-offset-2" data-testid="unknown-password-help-btn">
                {t('reg.nevimHeslo')}
              </button>
              {showPasswordHelp && (
                <div className="mt-1 p-2 bg-white rounded border border-bloom-violet/20 text-xs text-bloom-sub space-y-1" data-testid="password-help-text">
                  <p>{t('reg.jakZiskat')}</p>
                  <p>{t('reg.krok1')} {contactEmail && <a href={`mailto:${contactEmail}`} className="text-bloom-violet font-medium">{contactEmail}</a>}</p>
                  <p>{t('reg.krok2')}</p>
                  <p>{t('reg.krok3')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-bloom-text">{t('reg.prezdivka')}</Label>
          <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50" /><Input placeholder={t('reg.prezdivkaPriklad')} value={reg.username} onChange={(e) => updateReg('username', e.target.value)} className="pl-10" required minLength={3} data-testid="register-username-input" /></div>
        </div>
        <div className="space-y-2">
          <Label className="text-bloom-text">E-mail</Label>
          <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50" /><Input type="email" placeholder={t('auth.emailPriklad')} value={reg.email} onChange={(e) => updateReg('email', e.target.value)} className="pl-10" required data-testid="register-email-input" /></div>
        </div>
        <div className="space-y-2">
          <Label className="text-bloom-text">{t('reg.telefon')} <span className="text-bloom-sub text-xs">{t('reg.volitelne')}</span></Label>
          <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50" /><Input type="tel" placeholder="+420 xxx xxx xxx" value={reg.phone} onChange={(e) => updateReg('phone', e.target.value)} className="pl-10" data-testid="register-phone-input" /></div>
        </div>
        <div className="space-y-2">
          <Label className="text-bloom-text">{t('auth.heslo')}</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50" />
            <Input type={showPassword ? 'text' : 'password'} placeholder="Zadejte heslo" value={reg.password} onChange={(e) => updateReg('password', e.target.value)} className="pl-10 pr-10" required minLength={8} data-testid="register-password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-bloom-sub/50 hover:text-bloom-violet">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {reg.password.length > 0 && (
            <div className="space-y-0.5 mt-1" data-testid="reg-password-rules">
              {[
                { test: reg.password.length >= 8, label: t('reg.pozadavek8') },
                { test: /[A-Z]/.test(reg.password), label: t('reg.pozadavekVelke') },
                { test: /[0-9]/.test(reg.password), label: t('reg.pozadavekCislo') },
                { test: /[^a-zA-Z0-9]/.test(reg.password), label: t('reg.pozadavekZnak') },
              ].map((r, i) => (
                <div key={i} className={`flex items-center gap-1 text-[11px] ${r.test ? 'text-bloom-mint' : 'text-bloom-sub/50'}`}>
                  {r.test ? <span>&#10003;</span> : <span>&#9675;</span>} {r.label}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-bloom-text">{t('reg.potvrditHeslo')}</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50" />
            <Input type={showPassword ? 'text' : 'password'} placeholder="Zopakujte heslo" value={reg.passwordConfirm} onChange={(e) => updateReg('passwordConfirm', e.target.value)} className={`pl-10 ${reg.passwordConfirm && reg.password !== reg.passwordConfirm ? 'border-destructive' : ''}`} required data-testid="register-password-confirm-input" />
          </div>
          {reg.passwordConfirm && reg.password !== reg.passwordConfirm && <p className="text-xs text-destructive">{t('reg.hesloNesouhlasi')}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-bloom-text">{t('reg.zajmeno')}</Label>
            <Select value={reg.pronouns} onValueChange={(v) => updateReg('pronouns', v)}>
              <SelectTrigger data-testid="register-pronouns-select"><SelectValue /></SelectTrigger>
              <SelectContent>{pronounOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-bloom-text">{t('odb.kraj')}</Label>
            <div className="flex rounded-lg border border-border overflow-hidden">
              {[{ v: 'CZ', l: t('reg.cesko') }, { v: 'WORLD', l: t('reg.svet') }].map(x => (
                <button type="button" key={x.v} onClick={() => { setRegCountry(x.v); updateReg('location', 'none'); }}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${regCountry === x.v ? 'bg-bloom-violet text-white' : 'bg-white text-bloom-sub hover:bg-muted'}`}
                  data-testid={`reg-country-${x.v}`}>{x.l}</button>
              ))}
            </div>
            <Select value={reg.location || 'none'} onValueChange={(v) => updateReg('location', v)}>
              <SelectTrigger data-testid="register-location-select"><MapPin className="w-4 h-4 mr-1 text-bloom-sub/50" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('reg.nechciUvadet')}</SelectItem>
                {allLocations.filter(o => o.country === regCountry).map(o => (<SelectItem key={o.id || o.name} value={o.name}>{o.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-bloom-text">{t('reg.oMne')} <span className="text-bloom-sub text-xs">{t('reg.volitelne')}</span></Label>
          <Textarea placeholder={t('reg.oMnePriklad')} value={reg.bio} onChange={(e) => updateReg('bio', e.target.value)} rows={2} data-testid="register-bio-input" />
        </div>
        <div className="space-y-3">
          <Label className="text-bloom-text">{t('reg.avatar')}</Label>
          {avatarGroups.map(group => (
            <div key={group.label}>
              <p className={`text-xs font-medium ${group.color} mb-1.5`}>{group.label}</p>
              <div className="flex gap-2 flex-wrap">
                {group.avatars.map(option => (
                  <button key={option.value} type="button" onClick={() => { updateReg('avatar', option.value); setCustomAvatarPreview(null); }}
                    className={`rounded-full transition-all ${reg.avatar === option.value ? 'ring-2 ring-bloom-violet ring-offset-1 scale-110' : 'opacity-70 hover:opacity-100 hover:scale-105'}`}
                    title={t(option.klic)} data-testid={`avatar-${option.value}`}>
                    <Avatar className="w-10 h-10"><AvatarImage src={option.image} alt={t(option.klic)} /><AvatarFallback className="bg-bloom-violet text-white text-xs">{t(option.klic).charAt(0)}</AvatarFallback></Avatar>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <p className="text-xs text-bloom-sub mb-1.5">{t('reg.neboFoto')}</p>
            <div className="flex items-center gap-3">
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
              <button type="button" onClick={() => fileInputRef.current?.click()}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-all ${reg.avatar === 'custom' && customAvatarPreview ? 'border-bloom-violet bg-bloom-violet/5 text-bloom-violet' : 'border-border text-bloom-sub hover:border-bloom-violet'}`}
                data-testid="upload-avatar-btn"><Camera className="w-4 h-4" />{t('reg.nahratFoto')}</button>
              {customAvatarPreview && <Avatar className="w-10 h-10 ring-2 ring-bloom-violet"><AvatarImage src={customAvatarPreview} alt={t('reg.vlastni')} /></Avatar>}
            </div>
          </div>
        </div>
        {/* Honeypot */}
        <div className="absolute -left-[9999px]" aria-hidden="true" tabIndex={-1}>
          <input type="text" name="website" autoComplete="off" value={reg.website || ''} onChange={e => updateReg('website', e.target.value)} tabIndex={-1} />
        </div>
        <Button type="submit" className="w-full bg-bloom-violet text-white hover:bg-bloom-violet/90 font-semibold" disabled={loading} data-testid="register-submit-btn">
          {loading ? t('reg.registruji') : t('reg.registrovat')}
        </Button>
      </form>
      <div className="mt-4 pt-4 border-t border-border/50 space-y-2">
        <button type="button" onClick={() => {
            localStorage.setItem('bloom_google_mode', 'register');
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/auth/google-callback')}`;
          }}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-lg hover:bg-muted/50 transition-colors"
          data-testid="google-register-btn">
          {GOOGLE_SVG}
          <span className="text-sm font-medium text-bloom-text">{t('reg.presGoogle')}</span>
        </button>
      </div>
    </>
  );
}
